import React from 'react';
import { Select, Input, Tag, Tooltip, Alert } from 'antd';
import {
  InfoCircleOutlined,
  CodeOutlined, BulbOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const DEFAULT_ENGINEERING_CODE = `// 工程断言函数
// 参数：
//   aiOutput    - AI 输出的完整 JSON 对象
//   humanOutput - 人工决策的完整 JSON 对象
// 返回值：
//   { pass: boolean, reason: string }

function assert(aiOutput, humanOutput) {
  const aiDecision = aiOutput.decision;
  const humanDecision = humanOutput.human_decision;

  if (aiDecision === humanDecision) {
    return { pass: true, reason: \`决策一致：\${aiDecision}\` };
  }

  return {
    pass: false,
    reason: \`AI 决策 "\${aiDecision}" 与人工决策 "\${humanDecision}" 不一致\`
  };
}
`;

export function EngineeringAssertionForm({ config, onChange }) {
  const eng = config.engineering || {};

  const updateEngineering = (patch) => {
    onChange({ ...config, engineering: { ...eng, ...patch } });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CodeOutlined className="text-blue-500" />
        <span className="text-sm font-semibold text-gray-700">工程断言配置</span>
        <Tag color="blue" className="ml-1">JS 代码</Tag>
      </div>

      <Alert
        type="info"
        showIcon
        className="text-xs"
        message={
          <span className="text-xs text-gray-600">
            编写一个 <code className="bg-blue-50 px-1 rounded font-mono">assert(aiOutput, humanOutput)</code> 函数，接收 AI 和人工的完整 JSON 输出，返回 <code className="bg-blue-50 px-1 rounded font-mono">{'{ pass: boolean, reason: string }'}</code>
          </span>
        }
      />

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500">断言函数代码</span>
          <button
            onClick={() => updateEngineering({ code: DEFAULT_ENGINEERING_CODE })}
            className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer border-none bg-transparent"
          >
            重置为示例
          </button>
        </div>
        <div
          className="rounded-lg border border-gray-200 overflow-hidden"
          style={{ background: '#1e1e2e' }}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700" style={{ background: '#2a2a3e' }}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <span className="text-xs text-gray-400 font-mono">assertion.js</span>
          </div>
          <textarea
            value={eng.code || DEFAULT_ENGINEERING_CODE}
            onChange={(e) => updateEngineering({ code: e.target.value })}
            spellCheck={false}
            className="w-full font-mono text-xs leading-relaxed p-4 resize-none focus:outline-none"
            style={{
              background: '#1e1e2e',
              color: '#cdd6f4',
              minHeight: '260px',
              tabSize: 2,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                e.preventDefault();
                const start = e.target.selectionStart;
                const end = e.target.selectionEnd;
                const newValue = (eng.code || DEFAULT_ENGINEERING_CODE).substring(0, start) + '  ' + (eng.code || DEFAULT_ENGINEERING_CODE).substring(end);
                updateEngineering({ code: newValue });
                setTimeout(() => {
                  e.target.selectionStart = start + 2;
                  e.target.selectionEnd = start + 2;
                }, 0);
              }
            }}
          />
        </div>
        <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
          <InfoCircleOutlined />
          支持 ES6+ 语法，可使用 lodash（_）、dayjs 等常用工具库
        </div>
      </div>
    </div>
  );
}

export function LLMAssertionForm({ config, onChange, modelOptions = [] }) {
  const updateLLM = (patch) => {
    onChange({ ...config, llm: { ...config.llm, ...patch } });
  };

  const llm = config.llm || {};

  const PROMPT_TEMPLATES = [
    '判断 {ai_field} 与 {human_field} 在语义上是否表达相同的决策意图，忽略措辞差异',
    '对比 {ai_field} 和 {human_field}，若两者的核心结论一致则判定为一致，允许细节表述不同',
    '检查 {ai_field} 中的关键信息是否与 {human_field} 中的判断依据相符',
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <BulbOutlined className="text-purple-500" />
        <span className="text-sm font-semibold text-gray-700">LLM 断言配置</span>
        <Tag color="purple" className="ml-1">语义判断</Tag>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-500 mb-1.5">AI 输出字段路径 <span className="text-gray-400">(可选)</span></div>
          <Input
            placeholder="例：result.risk_clauses，留空则使用完整JSON"
            value={llm.aiField}
            onChange={(e) => updateLLM({ aiField: e.target.value })}
            className="font-mono text-xs"
            allowClear
          />
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1.5">人工决策字段路径 <span className="text-gray-400">(可选)</span></div>
          <Input
            placeholder="例：human_result.flagged_clauses，留空则使用完整JSON"
            value={llm.humanField}
            onChange={(e) => updateLLM({ humanField: e.target.value })}
            className="font-mono text-xs"
            allowClear
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-xs text-gray-500 flex items-center gap-1">
            语义对比描述
            <Tooltip title="描述 LLM 应如何判断两个字段是否一致，越具体越准确">
              <InfoCircleOutlined className="text-gray-400 cursor-help" />
            </Tooltip>
          </div>
        </div>
        <TextArea
          placeholder="描述判断标准，例如：判断 AI 决策字段与人工决策字段在语义上是否表达相同的审核结论，允许措辞不同但核心意图需一致..."
          value={llm.semanticDescription}
          onChange={(e) => updateLLM({ semanticDescription: e.target.value })}
          rows={4}
          className="text-sm"
        />
        <div className="mt-2">
          <div className="text-xs text-gray-400 mb-1.5">快速填入模板：</div>
          <div className="flex flex-wrap gap-1.5">
            {PROMPT_TEMPLATES.map((tpl, i) => (
              <button
                key={i}
                onClick={() => updateLLM({ semanticDescription: tpl })}
                className="text-xs px-2 py-1 bg-purple-50 text-purple-600 border border-purple-200 rounded hover:bg-purple-100 transition-colors cursor-pointer"
              >
                模板 {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs text-gray-500 mb-1.5">判断模型</div>
        <Select
          value={llm.model || 'qwen-max'}
          onChange={(val) => updateLLM({ model: val })}
          style={{ width: '100%' }}
          placeholder="请选择判断模型"
        >
          {modelOptions.length > 0 ? (
            modelOptions.map(model => (
              <Option key={model.value} value={model.value}>{model.label}</Option>
            ))
          ) : (
            <>
              <Option value="qwen-max">Qwen-Max（推荐）</Option>
              <Option value="qwen-plus">Qwen-Plus（快速）</Option>
              <Option value="gpt-4o">GPT-4o</Option>
            </>
          )}
        </Select>
      </div>
    </div>
  );
}
